import { cn } from '../lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  fullScreen?: boolean;
  message?: string;
}

export function LoadingSpinner({
  size = 'md',
  className,
  fullScreen = false,
  message,
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24',
  };

  const spinner = (
    <div className={cn('flex flex-col items-center justify-center gap-4', className)}>
      {/* iRelaxBot branded spinner */}
      <div className="relative">
        <div
          className={cn(
            'border-4 border-neutral-200 border-t-primary rounded-full animate-spin',
            sizeClasses[size]
          )}
        />
        {/* Inner rotating element for extra flair */}
        <div
          className={cn(
            'absolute inset-2 border-4 border-transparent border-b-secondary rounded-full animate-spin',
            'animation-delay-150'
          )}
          style={{ animationDirection: 'reverse', animationDuration: '1s' }}
        />
      </div>
      
      {message && (
        <p className="text-neutral-600 text-sm font-medium animate-pulse">
          {message}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
        {spinner}
      </div>
    );
  }

  return spinner;
}

// Skeleton loader for content
interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
}

export function Skeleton({
  className,
  variant = 'rectangular',
  width,
  height,
}: SkeletonProps) {
  const baseClasses = 'animate-pulse bg-neutral-200';
  
  const variantClasses = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], className)}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
    />
  );
}

// Product card skeleton
export function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-neutral-200">
      <Skeleton variant="rectangular" className="w-full h-64" />
      <div className="p-6 space-y-4">
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="40%" height={24} />
        <div className="flex gap-2">
          <Skeleton variant="rectangular" className="flex-1 h-10" />
          <Skeleton variant="rectangular" className="w-10 h-10" />
        </div>
      </div>
    </div>
  );
}

// Page loading fallback
export function PageLoader({ message }: { message?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" message={message || 'Loading...'} />
    </div>
  );
}
